import React, { useRef } from 'react';
import { makeStyles, tokens, Spinner, Field } from '@fluentui/react-components';
import AuthorizationAutocomplete from './AuthorizationAutocomplete';
import ThreadSelector, { ThreadSelectorRef } from './ThreadSelector';
import { useAuth } from '../auth/AuthProvider';
import { UserThread } from '../models/types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginLeft: tokens.spacingHorizontalL, // extra spacing from title
  },
  authField: {
    minWidth: '220px',
    maxWidth: '420px',
  },
  conversationField: {
    minWidth: '220px',
    maxWidth: '420px',
  },
  statusBadge: {
    display: 'none',
  },
});

interface HeaderControlsProps {
  authorizationId: string;
  selectedThreadId: string | null;
  threads: UserThread[];
  threadsLoading: boolean;
  isLoadingConversation: boolean;
  onAuthorizationChange: (authId: string, shouldLoadDocuments?: boolean) => Promise<void>;
  onThreadSelect: (threadId: string | null, authorizationId?: string) => Promise<void>;
  onRefreshThreads: () => Promise<UserThread[]>;
  uiMode?: string; // Domain for autocomplete (e.g., 'authorization', 'complaint')
}

/**
 * HeaderControls - Refactored to use props instead of custom events
 * - Renders the authorization autocomplete and conversation selector
 * - Uses standard React props and callbacks for communication
 * - No more custom events or complex state management
 */
const HeaderControls: React.FC<HeaderControlsProps> = ({
  authorizationId,
  selectedThreadId,
  threads,
  threadsLoading,
  isLoadingConversation,
  onAuthorizationChange,
  onThreadSelect,
  onRefreshThreads,
  uiMode = 'authorization',
}) => {
  const classes = useStyles();
  const threadSelectorRef = useRef<ThreadSelectorRef>(null);
  const auth = useAuth();
  const isAppealsMode = ['appeals', 'complaint'].includes(uiMode.toLowerCase());
  
  // Debug: log uiMode changes
  console.log(`[HeaderControls] Component mounted/updated with uiMode: "${uiMode}", lowercase domain: "${uiMode?.toLowerCase() || 'authorization'}"`);

  // Threads are now managed by ChatContainer, no need to load here

  // Only render header controls when the user is authenticated
  if (!auth?.isAuthenticated) {
    return null;
  }

  const handleAuthorizationChange = (value: string) => {
    // Just typing, don't load docs yet
    onAuthorizationChange(value, false);
  };

  const handleOptionSelected = (value: string) => {
    // When an option is selected from autocomplete, load docs
    onAuthorizationChange(value, true);
  };

  const handleThreadClear = () => {
    // Clear thread selection - start new conversation
    onThreadSelect(null);
  };

  const handleThreadSelect = (threadId: string | null, authorizationId?: string) => {
    if (threadId) {
      onThreadSelect(threadId, authorizationId);
    } else {
      handleThreadClear();
    }
  };

  return (
    <div className={classes.container}>
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
        <AuthorizationAutocomplete
          value={authorizationId}
          onChange={handleAuthorizationChange}
          onOptionSelected={handleOptionSelected}
          label={isAppealsMode ? 'Appeal ID' : 'Authorization ID'}
          placeholder={isAppealsMode ? 'Enter appeal ID (optional)...' : 'Enter authorization number (optional)...'}
          className={classes.authField}
          domain={uiMode?.toLowerCase() || 'authorization'}
        />
      </div>

      <div className={classes.conversationField}>
        <Field label="Conversation History">
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <ThreadSelector
                ref={threadSelectorRef}
                threads={threads}
                threadsLoading={threadsLoading}
                selectedThreadId={selectedThreadId || undefined}
                onThreadSelect={handleThreadSelect}
                onLoadConversation={handleThreadSelect}
                onClear={handleThreadClear}
                onRefreshThreads={onRefreshThreads}
                disabled={false}
              />
            </div>
            {isLoadingConversation && (
              <Spinner size="tiny" style={{ marginLeft: tokens.spacingHorizontalXS }} />
            )}
          </div>
        </Field>
      </div>
    </div>
  );
};

export default HeaderControls;